import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

const faqGroups = [
    {
        heading: 'General',
        items: [
            {
                q: '1. What is QRmor?',
                a: (
                    <>
                        <p>
                            QRmor is an anti-counterfeit QR code technology designed to help brands
                            protect their products and assets from copying and unauthorized replication.
                        </p>
                        <p>
                            Unlike conventional static or dynamic QR codes, QRmor codes are engineered to
                            be non-replicable and non-cloneable using multilayer cryptic technology at the
                            design level together with server-side authentication.
                        </p>
                        <p>
                            When a QRmor code is scanned, the authentication system verifies the scan and
                            can flag abnormal or duplicate scanning activity.
                        </p>
                    </>
                )
            },
            {
                q: '2. Why do normal QR codes fail in brand protection?',
                a: (
                    <>
                        <p>
                            Normal QR codes, whether static or dynamic, primarily function as information
                            carriers. They do not inherently provide strong anti-copy or anti-cloning
                            security.
                        </p>
                        <p>
                            A counterfeiter can copy a QR code from a genuine product and reproduce it on a
                            counterfeit product. When the copied code is scanned, it may still direct the
                            user to the same legitimate information as the original QR code.
                        </p>
                        <p>
                            This creates a significant limitation for brand protection: the QR code itself
                            does not necessarily establish whether the physical product carrying it is
                            genuine. QRmor addresses this limitation by combining the QR code with
                            authentication mechanisms designed to distinguish genuine scans from copied or
                            duplicated codes.
                        </p>
                    </>
                )
            },
            {
                q: '3. What is the difference between a normal QR code and a QRmor code?',
                a: (
                    <>
                        <div className="faq-table-wrap">
                            <table className="faq-table">
                                <thead>
                                    <tr>
                                        <th>Normal QR Code</th>
                                        <th>QRmor Code</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Primarily carries information</td><td>Designed for product authentication</td></tr>
                                    <tr><td>Can be copied and reproduced</td><td>Designed to be non-replicable</td></tr>
                                    <tr><td>Copied code may continue to work</td><td>Copied/duplicate scans can be flagged</td></tr>
                                    <tr><td>Does not inherently provide anti-counterfeit protection</td><td>Built specifically for anti-counterfeit applications</td></tr>
                                    <tr><td>Limited visibility into scan behaviour</td><td>Scan activity can be recorded and analysed</td></tr>
                                    <tr><td>Static or dynamic</td><td>Can be configured as static or dynamic</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <p>
                            The key difference is that QRmor is designed as an authentication and
                            anti-counterfeit technology rather than simply a QR code for information access.
                        </p>
                    </>
                )
            },
            {
                q: '4. What happens if someone copies a QRmor code?',
                a: (
                    <>
                        <p>
                            QRmor is designed so that simply reproducing the visual QR code does not
                            reproduce the authentication identity of the genuine product.
                        </p>
                        <p>
                            When a copied or duplicate QRmor code is scanned, the authentication server can
                            identify abnormal activity and flag the scan as potentially fake or duplicate.
                            This helps brands distinguish genuine products from products carrying copied QR
                            codes.
                        </p>
                    </>
                )
            },
            {
                q: '5. What are secure copy-proof QR codes?',
                a: (
                    <>
                        <p>
                            Secure copy-proof QR codes refer to QR codes designed with security mechanisms
                            that make unauthorized copying and replication substantially more difficult.
                        </p>
                        <p>
                            QRmor uses multilayer cryptic technology at the design level together with
                            server-side cryptography and authentication to create QR codes intended for
                            anti-counterfeit applications. Rather than relying only on the visual appearance
                            of the QR code, QRmor combines the code with authentication mechanisms on the
                            server side.
                        </p>
                    </>
                )
            },
            {
                q: '6. What is anti-copy QR code technology?',
                a: (
                    <>
                        <p>
                            Anti-copy QR code technology is technology designed to prevent a copied QR code
                            from being treated as an unquestioned duplicate of the genuine product.
                        </p>
                        <p>
                            Conventional QR codes can be visually copied relatively easily. QRmor is
                            designed to make the authentication behaviour of a copied code different from
                            that of the genuine code, which makes it suitable for applications where QR
                            codes need to contribute to brand protection and product authentication.
                        </p>
                    </>
                )
            },
            {
                q: '7. What are tamper-proof QR codes?',
                a: (
                    <>
                        <p>
                            The term tamper-proof QR codes generally refers to QR-based technologies
                            designed to make unauthorized alteration, duplication, or misuse more
                            difficult.
                        </p>
                        <p>
                            QRmor goes beyond the visual QR pattern by incorporating security mechanisms at
                            both the QR design level and authentication-server level, helping the system
                            identify suspicious or abnormal scan behaviour rather than relying solely on the
                            appearance of the printed code.
                        </p>
                    </>
                )
            },
            {
                q: '8. Are QRmor codes non-cloneable?',
                a: (
                    <p>
                        QRmor codes are designed to be non-cloneable and non-replicable through
                        multilayer cryptic technology and server-side authentication. The objective is
                        not simply to make the printed QR pattern difficult to reproduce — the QRmor
                        architecture is designed so that reproducing the visible code does not reproduce
                        the authentication behaviour of the genuine product.
                    </p>
                )
            },
            {
                q: '9. Does QRmor use a digital twin?',
                a: (
                    <p>
                        QRmor's authentication architecture can associate a QR code with product and
                        authentication information maintained on the server side. This creates a digital
                        representation of the product's authentication identity that can be used to
                        validate scans and record authentication activity — particularly useful for
                        non-cloneable QR codes and digital twin-based authentication applications.
                    </p>
                )
            },
            {
                q: '10. How is QRmor different from holograms?',
                a: (
                    <>
                        <p>
                            Holograms provide visual authentication and can make a product appear difficult
                            to reproduce. However, holographic artwork can potentially be reproduced by
                            counterfeiters through specialised printing or hologram vendors.
                        </p>
                        <p>
                            QRmor takes a different approach. A QRmor code is not intended to establish
                            authenticity merely through visual inspection — it uses QR-based authentication
                            supported by server-side cryptography. Even if someone reproduces the visual QR
                            code, the resulting scan can be evaluated by the authentication server and
                            abnormal or duplicate activity can be flagged.
                        </p>
                        <p style={{ fontStyle: 'italic', color: 'var(--ink)' }}>
                            In simple terms: a hologram primarily asks "Does this look genuine?" — QRmor
                            asks "Does this scan authenticate as genuine?"
                        </p>
                    </>
                )
            }
        ]
    },
    {
        heading: 'QRmor Technology & Features',
        items: [
            {
                q: '11. What makes QRmor codes non-replicable or non-cloneable?',
                a: (
                    <p>
                        QRmor uses multilayer cryptic technology at the design level together with
                        server-side cryptography and authentication. The technology incorporates security
                        into the QR generation and authentication process rather than depending solely on
                        the printed appearance of the QR code. QRmor codes are generated using a Texel
                        stochastic process, contributing to the technology's security architecture.
                    </p>
                )
            },
            {
                q: '12. What are the main features of QRmor?',
                a: (
                    <ul className="faq-list">
                        <li>Non-replicable / non-cloneable QR technology</li>
                        <li>Anti-counterfeit product authentication</li>
                        <li>Server-side authentication</li>
                        <li>Batch-wise information support</li>
                        <li>QR code generation using deep-tech stochastic processes</li>
                        <li>Pre-printed QR code convenience</li>
                        <li>Static or dynamic deployment options</li>
                        <li>No requirement for additional scanning hardware</li>
                        <li>No requirement for additional software installation</li>
                        <li>No requirement for online printing such as L1/L2 processes</li>
                        <li>Minimal changes to existing production processes</li>
                        <li>Geolocation capture during scanning</li>
                        <li>Date and time capture</li>
                        <li>Real-time scan activity reporting</li>
                        <li>Dashboard-based activity monitoring</li>
                        <li>Abnormal scan activity flagging</li>
                        <li>Audit trail generation</li>
                        <li>Plug-and-play deployment</li>
                        <li>Smartphone compatibility</li>
                        <li>No dedicated app required for scanning</li>
                    </ul>
                )
            },
            {
                q: '13. Can QRmor codes be used for different batches of the same SKU?',
                a: (
                    <p>
                        Yes. QRmor can be configured to support different deployment requirements,
                        including static or dynamic implementations. Depending on the implementation and
                        business requirement, a QRmor code can be configured to support multiple batches
                        of the same SKU. The exact configuration can be designed around the brand's
                        product and production workflow.
                    </p>
                )
            },
            {
                q: '14. Does QRmor require changes to the production line?',
                a: (
                    <p>
                        QRmor is designed for plug-and-play convenience and can be implemented without
                        requiring major changes to an existing production process. The exact integration
                        approach depends on the product, packaging process, and the brand's requirements.
                    </p>
                )
            },
            {
                q: '15. Does QRmor require additional hardware or software?',
                a: (
                    <p>
                        QRmor is designed to work without requiring brands to invest in additional
                        specialised scanning hardware. QRmor codes can be scanned using contemporary
                        smartphones, and no dedicated QRmor app is required for the end user to perform a
                        scan.
                    </p>
                )
            },
            {
                q: '16. Can QRmor codes be pre-printed?',
                a: (
                    <p>
                        Yes. QRmor supports pre-printed QR code deployment, providing convenience for
                        brands that want to incorporate QR codes into their packaging and production
                        workflow.
                    </p>
                )
            },
            {
                q: '17. What information is captured when a QRmor code is scanned?',
                a: (
                    <>
                        <p>Depending on the configured implementation, QRmor can capture information such as:</p>
                        <ul className="faq-list">
                            <li>Scan date</li>
                            <li>Scan time</li>
                            <li>Geolocation</li>
                            <li>Product/SKU information</li>
                            <li>Batch-related information</li>
                            <li>Authentication status</li>
                        </ul>
                        <p>This information can subsequently be used to understand product authentication activity.</p>
                    </>
                )
            },
            {
                q: '18. Can QRmor identify suspicious scanning activity?',
                a: (
                    <p>
                        Yes. QRmor's authentication system can analyse scanning activity and flag
                        abnormal or suspicious patterns, helping brands identify potential misuse, copied
                        codes, or unusual authentication behaviour.
                    </p>
                )
            },
            {
                q: '19. Does QRmor provide reports and dashboards?',
                a: (
                    <p>
                        Yes. QRmor can generate reports of scanning and authentication activity and
                        present relevant information through dashboards, allowing authorised users to
                        review authentication activity and identify unusual patterns.
                    </p>
                )
            },
            {
                q: '20. Does QRmor create an audit trail?',
                a: (
                    <p>
                        Yes. QRmor supports audit trail generation, allowing authentication activity to be
                        recorded for subsequent review and analysis.
                    </p>
                )
            },
            {
                q: '21. Can QRmor codes be scanned using a normal smartphone?',
                a: (
                    <p>
                        Yes. QRmor codes are designed to be scanned using contemporary smartphones. No
                        dedicated application is required for the end user to scan a QRmor code, making
                        the technology convenient for consumers, distributors, retailers, and other users
                        interacting with the product.
                    </p>
                )
            }
        ]
    },
    {
        heading: 'Industries',
        items: [
            {
                q: '22. Which industries can use QRmor?',
                a: (
                    <>
                        <p>
                            QRmor can be considered for a wide range of industries where brand protection,
                            product authentication, and anti-counterfeit measures are important. Potential
                            applications include:
                        </p>
                        <ul className="faq-list">
                            <li>Pharmaceuticals</li>
                            <li>FMCG</li>
                            <li>Luxury brands</li>
                            <li>Agriculture</li>
                            <li>Alcohol & wines</li>
                            <li>Jewellery</li>
                            <li>Beauty & cosmetics</li>
                            <li>Chemicals</li>
                            <li>Food & beverages</li>
                            <li>Lubricants & oils</li>
                            <li>Automobile spare parts</li>
                            <li>Other valuable or high-risk branded products</li>
                        </ul>
                    </>
                )
            },
            {
                q: '23. Is QRmor only for pharmaceutical companies?',
                a: (
                    <p>
                        No. Although pharmaceuticals are an important application area because of the
                        need for product authenticity and traceability, QRmor can be applied across many
                        industries where counterfeit products pose a business, consumer, or
                        brand-protection risk.
                    </p>
                )
            },
            {
                q: '24. Can QRmor support regulatory requirements?',
                a: (
                    <p>
                        QRmor can be configured to support industry and regulatory information
                        requirements, including requirements involving product, batch, and authentication
                        information. For regulated industries such as pharmaceuticals, the implementation
                        can be designed around applicable requirements and the organisation's specific
                        workflow.
                    </p>
                )
            }
        ]
    },
    {
        heading: 'Why Normal or Online Printed QRs Fail',
        items: [
            {
                q: 'Why do normal QR codes fail to protect brands from counterfeiting?',
                a: (
                    <p>
                        Normal QR codes are simple static pictures. They carry fixed information —
                        like a web link or batch number — but cannot distinguish between an original
                        printed mark and a cloned copy. Anyone can photograph a genuine QR code,
                        reprint it on a fake product, and it will scan normally.
                    </p>
                )
            },
            {
                q: 'Does encryption or online printed serial QRs make standard QR codes non-clonable?',
                a: (
                    <p>
                        No. Encryption secures only the data stored behind the code, not the physical
                        print itself. Counterfeiters do not need to break the encryption; taking a
                        photograph of the printed code makes a clone and bypasses the security
                        entirely. Such a copied QR will still scan normally and show genuine results.
                        So encryption or online-printed serial QRs cannot protect against
                        counterfeits.
                    </p>
                )
            },
            {
                q: 'What are the consequences of cloned QR codes?',
                a: (
                    <>
                        <p>
                            <b>For consumers:</b> They may unknowingly buy fake, unsafe goods —
                            like counterfeit medicine, food, or critical safety equipment — while
                            believing the scan verified its authenticity.
                        </p>
                        <p>
                            <b>For brands:</b> They suffer reputation damage, lose sales, lose
                            trust, and may face liability. Because standard QR codes cannot flag
                            copies, brands typically discover counterfeits long after the damage is
                            done, via complaints or returns.
                        </p>
                    </>
                )
            },
            {
                q: 'How does QRmor prevent QR codes from being copied?',
                a: (
                    <p>
                        QRmor generates dynamic authentication responses at the point of scanning.
                        Because it relies on underlying security behavior rather than just a static
                        visual image, photographing or reprinting the code fails to replicate the
                        authentication response.
                    </p>
                )
            },
            {
                q: 'What occurs when someone scans a counterfeit QRmor code?',
                a: (
                    <p>
                        The scan fails the real-time check and immediately displays a clear "not
                        verified" message to the user, preventing them from being fooled into
                        trusting a fake product.
                    </p>
                )
            },
            {
                q: 'How does server-side logging help track counterfeits?',
                a: (
                    <p>
                        Every scan attempt is checked and logged on the server with time and
                        available location details. This allows the system to detect scan anomalies
                        immediately and help brands locate counterfeit stock in the market in real
                        time.
                    </p>
                )
            }
        ]
    }
];

export default function FAQ() {
    useEffect(() => {
        window.scrollTo(0, 0)
        document.title = 'QRmor FAQ — Anti-Counterfeit, Non-Clonable, Secure QR Code Technology';
        const metaDesc = document.querySelector('meta[name="description"]');
        const content =
            'Frequently asked questions about QRmor: anti-counterfeit, non-clonable, secure QR code authentication for pharma and other regulated industries.';
        if (metaDesc) {
            metaDesc.setAttribute('content', content);
        } else {
            const tag = document.createElement('meta');
            tag.name = 'description';
            tag.content = content;
            document.head.appendChild(tag);
        }
    }, []);

    return (
        <>
            <header>
                <nav className="wrap">
                    <div className="brand">
                        <img src="/assets/QRmor.png" alt="QRmor logo" />
                    </div>
                    <Link to="/" className="btn btn-primary btn-sm">Back to home</Link>
                </nav>
            </header>

            <section>
                <div className="wrap legal-content" style={{ maxWidth: 860, padding: '48px 32px 80px' }}>
                    <span className="eyebrow">FAQ</span>
                    <h1 style={{ fontSize: 'clamp(28px, 4vw, 40px)', marginTop: 12, marginBottom: 36 }}>
                        QRmor — Anti-Counterfeit QR Technology FAQ
                    </h1>

                    {faqGroups.map((group) => (
                        <div key={group.heading} style={{ marginBottom: 44 }}>
                            <h2 style={{ fontSize: 22, marginBottom: 20, color: 'var(--crimson-deep)' }}>
                                {group.heading}
                            </h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                                {group.items.map((item) => (
                                    <div key={item.q}>
                                        <h3 style={{ fontSize: 17 }}>{item.q}</h3>
                                        <div style={{ color: 'var(--ink-soft)', marginTop: 8 }}>{item.a}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    <div className="faq-cta">
                        <h3>Looking for an anti-counterfeit QR solution?</h3>
                        <p>
                            If your brand is looking for secure copy-proof QR codes, anti-copy QR code
                            technology, non-cloneable QR codes, tamper-resistant QR authentication, or
                            anti-counterfeit QR codes, QRmor can help you design an authentication workflow
                            around your products and SKUs.
                        </p>
                        <p style={{ fontWeight: 700, color: 'var(--crimson-deep)', marginTop: 12 }}>
                            Let QRmor protect your Brands.
                        </p>
                        <Link to="/#contact" className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>
                            Talk to us about your product line and industry
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}